/**
 * Number formatting for the map panels.
 *
 * Spanish locale throughout — thousands separated by '.', decimals by ',' — to
 * match the rest of the product's user-facing strings.
 */

const NBSP = " ";

/**
 * A measurement, or an en dash when there is nothing to show.
 *
 * Null and NaN render as '–' rather than '0': these values come from a
 * statistical pipeline where "not computed for this building" and "computed as
 * zero" mean genuinely different things, and printing 0 for the first would
 * assert something the data does not say.
 */
export function fmt(
  value: number | null | undefined,
  decimals = 0,
  unit = "",
): string {
  if (value == null || !Number.isFinite(value)) return "–";

  const n = value.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  // Non-breaking space so a unit never wraps onto its own line in a narrow
  // sidebar cell.
  return unit ? `${n}${NBSP}${unit}` : n;
}

/** A whole number of things — dwellings, buildings, storeys. */
export function fmtCount(value: number | null | undefined, unit = ""): string {
  return fmt(value == null ? value : Math.round(value), 0, unit);
}

/** Euros, rounded: cents are noise at the scale these figures describe. */
export function fmtEuro(value: number | null | undefined): string {
  return fmt(value == null ? value : Math.round(value), 0, "€");
}

/** A percentage already expressed on a 0–100 scale. */
export function fmtPct(value: number | null | undefined, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return "–";
  return `${value.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
}
